// src/components/BuildingViews/WorkbenchView.js
import React, { useState } from 'react';
import styles from './WorkbenchView.module.css';
import { ItemData } from '../../data/ItemData';
import { getItemImageSrc } from '../../utils/getItemImageSrc';

const WorkbenchView = ({ playerData, onPlayerAction, onGoBack }) => {
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Get current crafting status
  const activeCrafting = playerData?.buildings?.workbench?.activeCrafting;

  const workbenchRecipes = [
    {
      id: 'craftFishingRod',
      name: 'Craft Fishing Rod',
      description: 'Create a basic fishing rod for catching fish',
      input: { 'wood': 5, 'string': 2 },
      output: { 'fishingRod': 1 },
      duration: 120, // 2 minutes
      icon: 'wearables/fishingRod.png'
    },
    {
      id: 'craftWoodenAxe',
      name: 'Craft Wooden Axe',
      description: 'Create a basic wooden axe for chopping wood',
      input: { 'wood': 8, 'string': 1 },
      output: { 'woodenAxe': 1 },
      duration: 90, // 1.5 minutes
      icon: 'wearables/woodenAxe.png'
    },
    {
      id: 'craftWoodenPickaxe',
      name: 'Craft Wooden Pickaxe',
      description: 'Create a basic wooden pickaxe for mining',
      input: { 'wood': 10, 'string': 1 },
      output: { 'woodenPickaxe': 1 },
      duration: 90, // 1.5 minutes
      icon: 'wearables/woodenPickaxe.png'
    },
    {
      id: 'craftWoodenSword',
      name: 'Craft Wooden Sword',
      description: 'Create a basic wooden sword for combat',
      input: { 'wood': 6, 'string': 1 },
      output: { 'woodenSword': 1 },
      duration: 75, // 1.25 minutes
      icon: 'wearables/woodenSword.png'
    }
  ];

  const canCraftRecipe = (recipe) => {
    for (const [itemKey, requiredAmount] of Object.entries(recipe.input)) {
      const available = playerData.inventory[itemKey] || 0;
      if (available < requiredAmount) {
        return false;
      }
    }
    return true;
  };

  const handleCraftClick = (recipe) => {
    if (!canCraftRecipe(recipe) || activeCrafting) {
      return;
    }

    setIsProcessing(true);
    setSelectedRecipe(recipe);
    
    // Call the action through the parent component
    onPlayerAction(recipe.id);
    
    // Reset processing state after a brief delay
    setTimeout(() => {
      setIsProcessing(false);
      setSelectedRecipe(null);
    }, 1000);
  };

  const handleStopCrafting = () => {
    onPlayerAction('stopCrafting', { buildingKey: 'workbench' });
  };

  const handleToggleContinuous = () => {
    onPlayerAction('toggleContinuousCrafting', { buildingKey: 'workbench' });
  };

  const buildingLevel = playerData.buildings?.workbench?.upgradeLevel || 0;

  return (
    <div className={styles.workbenchContainer}>
      {onGoBack && (
        <button 
          className={styles.backButton}
          onClick={onGoBack}
          title="Back to Home"
        >
          ✕
        </button>
      )}
      <div className={styles.workbenchHeader}>
        <img src="/img/buildingItems/workbench.png" alt="Workbench" className={styles.workbenchIcon} />
        <div className={styles.workbenchInfo}>
          <h2>Workbench Crafting</h2>
          <p>Level {buildingLevel}</p>
          <p className={styles.description}>
            Essential tools and basic items crafting station
          </p>
        </div>
      </div>

      {/* Active Crafting Progress */}
      {activeCrafting && (
        <div className={styles.craftingProgress}>
          <h3>🔨 Currently Crafting</h3>
          <div className={styles.activeRecipe}>
            <div className={styles.recipeInfo}>
              <span className={styles.recipeName}>
                {activeCrafting.actionConfig.output ? 
                  Object.keys(activeCrafting.actionConfig.output).map(key => 
                    ItemData[key]?.name || key
                  ).join(', ') :
                  ItemData[activeCrafting.actionConfig.outputItemKey]?.name || activeCrafting.actionConfig.outputItemKey
                }
              </span>
              <div className={styles.progressBar}>
                <div 
                  className={styles.progressFill} 
                  style={{ width: `${(activeCrafting.progress || 0) * 100}%` }}
                ></div>
              </div>
              <span className={styles.progressText}>
                {Math.floor((activeCrafting.progress || 0) * 100)}% Complete
              </span>
            </div>
            <div className={styles.craftingControls}>
              <button 
                onClick={handleToggleContinuous}
                className={`${styles.controlButton} ${activeCrafting.continuousCrafting ? styles.active : ''}`}
              >
                {activeCrafting.continuousCrafting ? '🔄 Continuous ON' : '🔄 Continuous OFF'}
              </button>
              <button 
                onClick={handleStopCrafting}
                className={`${styles.controlButton} ${styles.stopButton}`}
              >
                ⏹️ Stop
              </button>
            </div>
          </div>
        </div>
      )}

      <div className={styles.recipeGrid}>
        {workbenchRecipes.map((recipe) => {
          const canCraft = canCraftRecipe(recipe);
          const isSelected = selectedRecipe?.id === recipe.id;
          
          return (
            <div 
              key={recipe.id} 
              className={`${styles.recipeCard} ${!canCraft ? styles.disabled : ''} ${isSelected ? styles.selected : ''}`}
            >
              <div className={styles.recipeHeader}>
                <img 
                  src={getItemImageSrc(recipe.icon)} 
                  alt={recipe.name} 
                  className={styles.recipeIcon} 
                />
                <div className={styles.recipeInfo}>
                  <h3>{recipe.name}</h3>
                  <p>{recipe.description}</p>
                </div>
              </div>

              <div className={styles.recipeDetails}>
                <div className={styles.requirements}>
                  <h4>Required:</h4>
                  <div className={styles.itemList}>
                    {Object.entries(recipe.input).map(([itemKey, amount]) => {
                      const item = ItemData[itemKey];
                      const available = playerData.inventory[itemKey] || 0;
                      const hasEnough = available >= amount;
                      
                      return (
                        <div key={itemKey} className={`${styles.itemRequirement} ${!hasEnough ? styles.insufficient : ''}`}>
                          <img 
                            src={getItemImageSrc(itemKey)} 
                            alt={item?.name || itemKey} 
                            className={styles.itemIcon} 
                          />
                          <span>{item?.name || itemKey}</span>
                          <span className={styles.itemAmount}>
                            {available}/{amount}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className={styles.output}>
                  <h4>Produces:</h4>
                  <div className={styles.itemList}>
                    {Object.entries(recipe.output).map(([itemKey, amount]) => {
                      const item = ItemData[itemKey];
                      
                      return (
                        <div key={itemKey} className={styles.itemOutput}>
                          <img 
                            src={getItemImageSrc(itemKey)} 
                            alt={item?.name || itemKey} 
                            className={styles.itemIcon} 
                          />
                          <span>{item?.name || itemKey}</span>
                          <span className={styles.itemAmount}>x{amount}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className={styles.recipeStats}>
                  <div className={styles.duration}>
                    <span>⏱️ {recipe.duration}s</span>
                  </div>
                </div>
              </div>

              <button 
                onClick={() => handleCraftClick(recipe)}
                disabled={!canCraft || isProcessing || activeCrafting}
                className={`${styles.craftButton} ${isProcessing && isSelected ? styles.processing : ''}`}
              >
                {activeCrafting ? 'Workbench Busy' : (isProcessing && isSelected ? 'Crafting...' : 'Start Crafting')}
              </button>
            </div>
          );
        })}
      </div>

      <div className={styles.workbenchTips}>
        <h4>🔨 Workbench Tips</h4>
        <ul>
          <li>The workbench is perfect for crafting basic tools</li>
          <li>Great for creating fishing rods and simple equipment</li>
          <li>More recipes will be available as you progress</li>
        </ul>
      </div>
    </div>
  );
};

export default WorkbenchView; 